import keycodeData from '@/utils/keycode.json'
import i18n from '@/locales'

import { WHEEL_KEY_CODE, MOUSE_KEY_CODE, type MacroWheelDirection } from '../types'

type KeycodePlatform = 'windows' | 'linux' | 'macx'

const tk = (key: string, named?: Record<string, unknown>) =>
  (named ? i18n.global.t(key, named) : i18n.global.t(key)) as string

type KeycodeLocalizedText = string | Record<string, string | undefined>

interface KeycodeMetaItem {
  name?: KeycodeLocalizedText
  alias?: KeycodeLocalizedText
  code?: string | number
  type?: string | number
  sys_code?: Partial<Record<KeycodePlatform, string[]>>
}

interface KeycodeDataRoot {
  key_code_default?: {
    key?: KeycodeMetaItem[]
  }
}

const KEYCODE_PLATFORMS: KeycodePlatform[] = ['windows', 'macx', 'linux']

const keycodeItems = ((keycodeData as KeycodeDataRoot).key_code_default?.key ??
  []) as KeycodeMetaItem[]

const toNumber = (value: string | number | undefined) => {
  if (value == null) return undefined
  if (typeof value === 'number') return value

  const result = Number.parseInt(value, 0)
  return Number.isNaN(result) ? undefined : result
}

// 解析sys_code
const parseSysCode = (value: string | number | undefined) => {
  if (value == null) return undefined
  if (typeof value === 'number') return value

  const trimmed = value.trim()
  if (!trimmed || trimmed.toUpperCase() === 'NULL') return undefined

  const result = Number.parseInt(trimmed, 0)
  return Number.isNaN(result) ? undefined : result
}

const normalizeLocalizedText = (value: KeycodeLocalizedText | undefined): string | undefined => {
  if (typeof value === 'string') {
    const normalized = value.trim()
    return normalized || undefined
  }

  if (value && typeof value === 'object') {
    const preferred = value.standard ?? Object.values(value).find((item) => !!item?.trim())
    return preferred?.trim() || undefined
  }

  return undefined
}

const toTypeNumber = (type: string | number | undefined) => {
  if (type == null) return undefined
  if (typeof type === 'number') return type

  const result = Number.parseInt(type, 0)
  return Number.isNaN(result) ? undefined : result
}

const isNormalKeyboardType = (item: KeycodeMetaItem) => {
  return toTypeNumber(item.type) === 1
}

const getItemWeight = (item: KeycodeMetaItem) => {
  return isNormalKeyboardType(item) ? 2 : 1
}

const resolveItemLabel = (item: KeycodeMetaItem, hidCode: number) => {
  return (
    normalizeLocalizedText(item.alias) ??
    normalizeLocalizedText(item.name) ??
    `0x${hidCode.toString(16).toUpperCase()}`
  )
}

// hid code 按键映射
const HID_CODE_TO_ITEM = new Map<number, KeycodeMetaItem>()
// sys_code 各个系统下的 按键映射
const SYS_VKEY_TO_ITEM: Record<KeycodePlatform, Map<number, KeycodeMetaItem>> = {
  windows: new Map<number, KeycodeMetaItem>(),
  macx: new Map<number, KeycodeMetaItem>(),
  linux: new Map<number, KeycodeMetaItem>(),
}

for (const item of keycodeItems) {
  const hidCode = toNumber(item.code)
  if (hidCode != null) {
    // 查找 hid code 表是否存在该按键值
    const existing = HID_CODE_TO_ITEM.get(hidCode)
    if (!existing || getItemWeight(item) > getItemWeight(existing)) {
      HID_CODE_TO_ITEM.set(hidCode, item)
    }
  }

  for (const platform of KEYCODE_PLATFORMS) {
    // Keyboard recording only accepts "normal key" definitions (type=1).
    if (!isNormalKeyboardType(item)) continue

    const sysCodes = item.sys_code?.[platform] ?? []
    for (const sysCodeRaw of sysCodes) {
      const vKeyCode = parseSysCode(sysCodeRaw)
      if (vKeyCode == null || hidCode == null) continue

      const existing = SYS_VKEY_TO_ITEM[platform].get(vKeyCode)
      if (!existing || getItemWeight(item) > getItemWeight(existing)) {
        SYS_VKEY_TO_ITEM[platform].set(vKeyCode, item)
      }
    }
  }
}

const HID_CODE_TO_LABEL = new Map<number, string>()
for (const [hidCode, item] of HID_CODE_TO_ITEM) {
  HID_CODE_TO_LABEL.set(hidCode, resolveItemLabel(item, hidCode))
}

const SYS_VKEY_TO_HID_CODE: Record<KeycodePlatform, Map<number, number>> = {
  windows: new Map<number, number>(),
  macx: new Map<number, number>(),
  linux: new Map<number, number>(),
}

for (const platform of KEYCODE_PLATFORMS) {
  for (const [vKeyCode, item] of SYS_VKEY_TO_ITEM[platform]) {
    const hidCode = toNumber(item.code)
    if (hidCode != null) {
      SYS_VKEY_TO_HID_CODE[platform].set(vKeyCode, hidCode)
    }
  }
}

/**
 * 特殊按键的code在event.keyCode/which 获取到的值是和sys_code不一样的
 * 不随键盘布局而改变的按键值
 */
const CODE_FALLBACK_TO_HID: Record<string, number> = {
  ShiftLeft: 0xe1,
  ShiftRight: 0xe5,
  ControlLeft: 0xe0,
  ControlRight: 0xe4,
  AltLeft: 0xe2,
  AltRight: 0xe6,
  MetaLeft: 0xe3,
  MetaRight: 0xe7,
  ContextMenu: 0x65,
  NumpadEnter: 0x58,
}

const getMouseCodeLabel = (code: number) => {
  const labelKeys: Partial<Record<number, string>> = {
    [MOUSE_KEY_CODE.left]: 'keys.media.mouseLeft',
    [MOUSE_KEY_CODE.right]: 'keys.media.mouseRight',
    [MOUSE_KEY_CODE.middle]: 'keys.media.mouseMiddle',
    [MOUSE_KEY_CODE.back]: 'keys.media.mouseBack',
    [MOUSE_KEY_CODE.forward]: 'keys.media.mouseForward',
  }

  const key = labelKeys[code]
  return key ? tk(key) : undefined
}

// 查找运行时系统，需要确定当前键盘按键的sys_code
const detectRuntimePlatform = (): KeycodePlatform => {
  const runtimeProcess = (globalThis as typeof globalThis & { process?: { platform?: string } })
    .process

  if (runtimeProcess?.platform === 'darwin') return 'macx'
  if (runtimeProcess?.platform === 'linux') return 'linux'
  if (runtimeProcess?.platform === 'win32') return 'windows'

  if (typeof navigator === 'undefined') return 'windows'

  const uaData = navigator as Navigator & { userAgentData?: { platform?: string } }
  const rawPlatform = `${uaData.userAgentData?.platform ?? ''} ${navigator.platform ?? ''} ${
    navigator.userAgent ?? ''
  }`.toLowerCase()

  if (rawPlatform.includes('mac')) return 'macx'
  if (rawPlatform.includes('linux') || rawPlatform.includes('x11')) return 'linux'
  return 'windows'
}

/**
 * 从键盘事件中读取 event.code (针对一些固定按键，见CODE_FALLBACK_TO_HID)
 */
export const resolveKeyboardHidFromEvent = (event: KeyboardEvent): number | undefined => {
  const codeMapped = CODE_FALLBACK_TO_HID[event.code]
  if (codeMapped != null) return codeMapped

  // 极端特殊情况，shiftRight在keyboardEvent没有code，故用key判断
  if (event.key === 'Shift' && event.keyCode === 16) return CODE_FALLBACK_TO_HID['ShiftRight']

  // 获取系统层的按键code，对应sys_code
  const legacyKeyCode = event.keyCode || (event as KeyboardEvent & { which?: number }).which
  if (legacyKeyCode == null) return undefined

  // 当前平台
  const platform = detectRuntimePlatform()
  const mappedByPlatform = SYS_VKEY_TO_HID_CODE[platform].get(legacyKeyCode)
  if (mappedByPlatform != null) return mappedByPlatform

  return SYS_VKEY_TO_HID_CODE.windows.get(legacyKeyCode)
}

export const getMacroKeyLabelByCode = (code: number): string => {
  const mouseLabel = getMouseCodeLabel(code)
  if (mouseLabel) return mouseLabel
  if (code === WHEEL_KEY_CODE) {
    return '滚轮'
  }

  return HID_CODE_TO_LABEL.get(code) ?? `0x${code.toString(16).toUpperCase()}`
}

export const getMouseHidCodeByButton = (button: number): number | undefined => {
  if (button === 0) return MOUSE_KEY_CODE.left
  if (button === 1) return MOUSE_KEY_CODE.middle
  if (button === 2) return MOUSE_KEY_CODE.right
  if (button === 3) return MOUSE_KEY_CODE.back
  if (button === 4) return MOUSE_KEY_CODE.forward
  return undefined
}

export const resolveWheelDirectionFromEvent = (
  event: Pick<WheelEvent, 'deltaX' | 'deltaY'>,
): { vWheel: number; hWheel: number } => {
  const wheels = {
    vWheel: 0,
    hWheel: 0,
  }
  if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
    if (event.deltaX > 0) wheels.hWheel = -1
    else wheels.hWheel = 1
  }

  if (event.deltaY > 0) wheels.vWheel = -1
  else if (event.deltaY < 0) wheels.vWheel = 1

  return wheels
}

export const getWheelDirectionLabel = (direction: MacroWheelDirection) => {
  if (direction.hWheel === 0) {
    return direction.vWheel === -1 ? '后滚' : '前滚'
  } else {
    return direction.hWheel === -1 ? '右滚' : '左滚'
  }
}
